import { endOfYear, startOfYear } from 'date-fns'
import { Factory } from 'fishery'
import { v4 as uuidV4 } from 'uuid'
import { OpalApiResponseDocument } from '@opallabs/api'
import { prePlanningBlockKey } from '@opallabs/api/BFF'
import { nowISOString, formatToDateString } from '../../util/datestring'
import { generateListUuidLists } from '../../util/generateUuidList'
import { keyDate } from '../components/key_date'
import {
  composeBlocksByCategory,
  composeBlocksById,
} from '../components/normalized_block'
import {
  categoryHierarchyFactory,
  composeCategoriesFromHierarchy,
  extractHierarchyIds,
} from '../components/normalized_category'

type Plan = OpalApiResponseDocument<'ExperimentalReadPlanBFFV1', 200>

type BlockIdsWithCategoryId = {
  blockId: string
  categoryId: string | null
  rowIndex: number
}

const mapCategoryIdsToBlock = (blockIds: string[][], categoryIds: string[]) => {
  // ignore first entry which is parent category & add null to represent preplanning blocks
  const childCategoryIds = [...categoryIds.toSpliced(0, 1), null]

  return blockIds.reduce((accum, current, i) => {
    const categoryId = childCategoryIds[i]
    const withIds = current.map((id, i) => ({
      blockId: id,
      categoryId,
      rowIndex: i + 1,
    }))
    return [...accum, ...withIds]
  }, [] as BlockIdsWithCategoryId[])
}

export const planFactory: Factory<Plan> = Factory.define<Plan>(
  ({ sequence, params }) => {
    const hierarchy = params.data?.categories?.hierarchy ?? [
      categoryHierarchyFactory.build({
        children: [
          categoryHierarchyFactory.build(),
          categoryHierarchyFactory.build(),
        ],
      }),
      categoryHierarchyFactory.build(),
      categoryHierarchyFactory.build({ id: prePlanningBlockKey }),
    ]
    const blockIds = generateListUuidLists(5, 2)
    const categoryIds = extractHierarchyIds(hierarchy)
    const categoriesById = composeCategoriesFromHierarchy(hierarchy)
    const blockIdsWithCategoryId = mapCategoryIdsToBlock(blockIds, categoryIds)

    return {
      data: {
        id: uuidV4(),
        created_at: nowISOString(),
        updated_at: nowISOString(),
        kind: 'plan' as const,
        parent_block_id: uuidV4(),
        parent_block_view_id: null,
        title: `Mock Plan ${sequence}`,
        description: `Description for Mock Plan ${sequence}`,
        owner_id: uuidV4(),
        effective_privacy: 'whole_workspace',
        is_private: false,
        blocks: {
          by_id: composeBlocksById(blockIdsWithCategoryId),
          by_category_with_position: composeBlocksByCategory(
            blockIds,
            categoryIds
          ),
        },
        categories: {
          by_id: categoriesById,
          hierarchy,
        },
        charts: {
          by_id: {},
        },
        custom_field_values: {},
        key_dates: [keyDate.build()],
        settings: {
          category_type: null,
          colors: [
            {
              id: 'c558f617-c9d1-4ff6-ad4c-c0f90c863d38',
              label: 'pink',
              color: '#facade',
            },
          ],
          default_timescale: 'month' as const,
          start_at: formatToDateString(startOfYear(new Date())),
          end_at: formatToDateString(endOfYear(new Date())),
          start_month_index: 0 as const,
          start_weekday_index: 0 as const,
          valid_start_at: formatToDateString(startOfYear(new Date())),
          valid_end_at: formatToDateString(endOfYear(new Date())),
          show_smart_swimlane_datasource: true,
          swimlane_title_column_width: 200,
        },
      },
    } satisfies Plan
  }
)
